import { cn } from '@/lib/cn';
import type { StatusIndicatorSize } from '@heidi/design-system/status-indicator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { typographyVariant } from '@heidi/design-system/typography';
import { useIntl } from 'react-intl';

import {
  offlineSyncStatusBadgeConfig,
  offlineSyncStatusMessages,
  syncStatusToneTextClass,
} from './sync-status-badge.config';
import type {
  SyncStatusBadgeConfig,
  SyncStatusBadgeProps,
} from './sync-status-badge.types';

/** lucide glyph footprint per size. */
const lucideSizeClass: Record<StatusIndicatorSize, string> = {
  xs: 'size-4',
  sm: 'size-6',
  md: 'size-8',
};

const renderGlyph = (
  { glyph, tone }: SyncStatusBadgeConfig,
  size: StatusIndicatorSize
) => {
  const Icon = glyph.icon;
  return (
    <Icon
      aria-hidden
      className={cn(
        lucideSizeClass[size],
        syncStatusToneTextClass[tone],
        // `motion-safe` only: reduced-motion users get a static glyph; the
        // "Syncing…" label still conveys progress (animations guideline).
        glyph.spin && 'motion-safe:animate-spin'
      )}
    />
  );
};

/**
 * Read-only badge for an offline recording's sync status — Marc's literal Figma
 * design: a bare `lucide-react` glyph tinted by tone, with
 * a visible label only for the in-flight ("Syncing…") and `synced` ("Synced")
 * states. `pending_sync`, every failure, and `recording` are icon-only with a
 * Tooltip whose copy doubles as the glyph's `aria-label` (the inner SVG is
 * `aria-hidden`).
 *
 * Pure and prop-driven — it represents whatever `status` it is given and holds
 * no recorder / list / connectivity logic, so the same component renders in both
 * the session-list row and the session-detail header. (Both surfaces suppress
 * `recording` upstream — that's the recorder's affordance, not this badge.)
 *
 * Non-interactive: the icon-only wrapper is a labelled `role="img"` (no tab
 * stop), so the tooltip surfaces on hover and assistive tech still gets the
 * accessible name.
 *
 * Scope: the badge's display vocabulary (`SyncBadgeStatus`) — every locked
 * `sync_tracking` state, plus `note_failed`, which callers resolve from durable
 * client state rather than from the queue. `sync_interrupted` is not
 * represented: it is a Chronicle transfer state with no analogue here.
 */
export const SyncStatusBadge = ({
  status,
  size = 'xs',
  hideLabel = false,
  messageOverride,
  className,
  'data-testid': dataTestId,
}: SyncStatusBadgeProps) => {
  const intl = useIntl();
  const config = offlineSyncStatusBadgeConfig[status];
  const message =
    messageOverride ?? intl.formatMessage(offlineSyncStatusMessages[status]);
  const glyph = renderGlyph(config, size);

  // Force icon-only for the list; see the hideLabel prop JSDoc.
  const presentation = hideLabel ? 'iconOnly' : config.presentation;

  if (presentation === 'iconOnly') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="img"
            aria-label={message}
            className={cn('inline-flex', syncStatusToneTextClass[config.tone], className)}
            data-testid={dataTestId}
          >
            {glyph}
          </span>
        </TooltipTrigger>
        <TooltipContent>{message}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1',
        syncStatusToneTextClass[config.tone],
        className
      )}
      data-testid={dataTestId}
    >
      {glyph}
      <span className={typographyVariant({ variant: 'caption' })}>{message}</span>
    </span>
  );
};

SyncStatusBadge.displayName = 'SyncStatusBadge';
